import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AuthContext from '../../context/authContext';
import useMenuItem from '../../hooks/useMenuItem';
import useBranch from '../../hooks/useBranch';
import useUser from '../../hooks/useUser';
import { currencyFormatter } from '../../util/formatHelper';

// Management of venue menu items. Menu items are not free-form: they are existing
// upsells (upgrades without the 'hasPackage' tag) flagged per branch via the
// upsell's hasAsMenuItem array — there is no separate menu-item collection and all
// fields come straight from the upgrade.
// - Full `menuItem:assign` right: edit every upsell and pick any branch(es) it is
//   offered for (multi-select).
// - `menuItem:assign:own` right: scoped to the user's assigned branch(es); only
//   that branch's membership can be toggled, the rest is left untouched server-side.
const mapBranch = b => ({
  _id: typeof b._id === 'string' ? b._id : String(b._id),
  label: b.locationName || b.longName || 'Standort',
});

const formatPrice = value => currencyFormatter.format(value ?? 0);

const MenuItems = () => {
  const theme = useTheme();
  // Below md the five-column table cannot fit; a stacked card list is used instead.
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const authCtx = useContext(AuthContext);
  const canFull = authCtx.checkRight('menuItem:assign');
  const canOwn = authCtx.checkRight('menuItem:assign:own');
  const canView = canFull || canOwn;
  const ownOnly = canOwn && !canFull;

  const { getMenuItems, updateMenuItemBranches } = useMenuItem();
  const { getBranches } = useBranch();
  const { getMyBranches } = useUser();

  const [upsells, setUpsells] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingIds, setSavingIds] = useState({});
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);

  // Branch ids the caller may actually change. For full callers this is every
  // branch; for own callers it is the assigned branches (or, if none are
  // assigned, every branch = full access fallback, matching the backend).
  const editableBranchIds = useMemo(
    () => new Set(branchOptions.map(b => b._id)),
    [branchOptions]
  );

  const getMenuItemsRef = useRef(getMenuItems);
  const getBranchesRef = useRef(getBranches);
  const getMyBranchesRef = useRef(getMyBranches);
  useEffect(() => {
    getMenuItemsRef.current = getMenuItems;
  }, [getMenuItems]);
  useEffect(() => {
    getBranchesRef.current = getBranches;
  }, [getBranches]);
  useEffect(() => {
    getMyBranchesRef.current = getMyBranches;
  }, [getMyBranches]);

  // Load the list of branches the caller can assign to.
  useEffect(() => {
    let cancelled = false;
    const loadFullBranchList = async () => {
      const list = await getBranchesRef.current();
      if (!cancelled) setBranchOptions((list || []).map(mapBranch));
    };
    (async () => {
      if (!canView) return;
      try {
        if (canFull) {
          await loadFullBranchList();
          return;
        }
        const r = await getMyBranchesRef.current();
        if (cancelled) return;
        const assignedIds = (r?.branchIds || []).filter(Boolean);
        if (assignedIds.length > 0) {
          setBranchOptions((r?.branches || []).map(mapBranch));
        } else {
          // No assigned branches → fall back to the full list (full access).
          await loadFullBranchList();
        }
      } catch (_) {
        if (!cancelled) toast.error('Fehler beim Laden der Standorte');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView, canFull]);

  const loadUpsells = useCallback(async searchTerm => {
    setLoading(true);
    try {
      const r = await getMenuItemsRef.current(searchTerm || '');
      const list = (r?.upsells || []).map(u => ({
        ...u,
        _id: String(u._id),
        hasAsMenuItem: (u.hasAsMenuItem || []).map(id => String(id)),
      }));
      setUpsells(list);
    } catch (_) {
      toast.error('Fehler beim Laden der Menüpunkte');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canView) return;
    loadUpsells(search);
  }, [canView, search, loadUpsells]);

  const applySearch = () => {
    const value = searchRef.current ? searchRef.current.value.trim() : '';
    setSearch(value);
  };

  // Persist a new menu-item branch selection for one upsell. `branchIds` is the
  // desired selection within the caller's authority (full set for full callers,
  // own-branch subset for own callers); the server returns the resulting full
  // hasAsMenuItem array which we store back.
  const saveBranches = useCallback(
    async (upgradeId, branchIds) => {
      setSavingIds(prev => ({ ...prev, [upgradeId]: true }));
      try {
        const r = await updateMenuItemBranches(upgradeId, branchIds);
        const updated = (r?.hasAsMenuItem || []).map(id => String(id));
        setUpsells(prev =>
          prev.map(u => (u._id === upgradeId ? { ...u, hasAsMenuItem: updated } : u))
        );
        toast.success('Menüpunkt aktualisiert');
      } catch (_) {
        toast.error('Fehler beim Aktualisieren des Menüpunkts');
      } finally {
        setSavingIds(prev => {
          const next = { ...prev };
          delete next[upgradeId];
          return next;
        });
      }
    },
    [updateMenuItemBranches]
  );

  if (!canView) {
    return <Navigate to='/' replace />;
  }

  const singleOwnBranch = ownOnly && branchOptions.length === 1 ? branchOptions[0] : null;

  const renderAssignmentControl = upsell => {
    const saving = !!savingIds[upsell._id];
    const assigned = upsell.hasAsMenuItem || [];

    // Own caller with exactly one assigned branch → a simple on/off switch.
    if (singleOwnBranch) {
      const checked = assigned.includes(singleOwnBranch._id);
      return (
        <Stack direction='row' alignItems='center' spacing={1}>
          <Switch
            checked={checked}
            disabled={saving}
            onChange={() =>
              saveBranches(upsell._id, checked ? [] : [singleOwnBranch._id])
            }
          />
          <Typography variant='body2' color='text.secondary'>
            {singleOwnBranch.label}
          </Typography>
          {saving && <CircularProgress size={16} />}
        </Stack>
      );
    }

    // Full caller, or own caller with several assigned branches → multi-select of
    // the branches the caller may manage. For own callers the value reflects only
    // the caller's own branches; the server preserves the rest.
    const value = assigned.filter(id => editableBranchIds.has(id));
    return (
      <Stack direction='row' alignItems='center' spacing={1} sx={{ width: '100%' }}>
        <FormControl
          size='small'
          sx={{ minWidth: { xs: 0, md: 260 }, width: { xs: '100%', md: 'auto' } }}
        >
          <Select
            multiple
            displayEmpty
            value={value}
            disabled={saving}
            onChange={e => {
              const selected =
                typeof e.target.value === 'string'
                  ? e.target.value.split(',')
                  : e.target.value;
              saveBranches(upsell._id, selected);
            }}
            renderValue={selected => {
              if (!selected || selected.length === 0) {
                return (
                  <Typography variant='body2' color='text.secondary'>
                    Keine Standorte
                  </Typography>
                );
              }
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(id => {
                    const opt = branchOptions.find(b => b._id === id);
                    return <Chip key={id} label={opt ? opt.label : id} size='small' />;
                  })}
                </Box>
              );
            }}
          >
            {branchOptions.map(opt => (
              <MenuItem key={opt._id} value={opt._id}>
                <Checkbox checked={value.indexOf(opt._id) > -1} />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {saving && <CircularProgress size={16} />}
      </Stack>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant='h4' gutterBottom>
        Speisekarte
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Eine Speise ist ein bestehender Upsell (kein Paket), der einem Standort
        zugeordnet wird. Name, Preis, Bild, Kategorie und Allergene werden am Upsell
        gepflegt – hier wird nur festgelegt, an welchen Standorten der Upsell als
        Speise auf der Speisekarte erscheint.{' '}
        {ownOnly
          ? 'Du kannst Upsells für deinen Standort auf die Speisekarte setzen.'
          : 'Wähle pro Upsell die Standorte aus, für die er als Speise gilt.'}
      </Typography>

      <Stack
        direction='row'
        spacing={1}
        useFlexGap
        flexWrap='wrap'
        alignItems='center'
        sx={{ mb: 2 }}
      >
        <TextField
          inputRef={searchRef}
          size='small'
          placeholder='Upsells durchsuchen'
          defaultValue={search}
          onKeyDown={e => {
            if (e.key === 'Enter') applySearch();
          }}
          sx={{ minWidth: { xs: 0, sm: 280 }, flexGrow: { xs: 1, sm: 0 } }}
        />
        <Button variant='outlined' onClick={applySearch}>
          Suchen
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : upsells.length === 0 ? (
        <Typography variant='body1' color='text.secondary'>
          Keine Upsells gefunden.
        </Typography>
      ) : isSmallScreen ? (
        <Stack spacing={1.5}>
          {upsells.map(upsell => (
            <Paper key={upsell._id} sx={{ p: 2 }}>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='baseline'
                spacing={1}
              >
                <Typography variant='body1' fontWeight={600}>
                  {upsell.name}
                </Typography>
                <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                  {formatPrice(upsell.price)}
                </Typography>
              </Stack>
              {upsell.category && (
                <Typography variant='body2' color='text.secondary'>
                  {upsell.category}
                </Typography>
              )}
              {(upsell.descriptionShort || upsell.description) && (
                <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                  {upsell.descriptionShort || upsell.description}
                </Typography>
              )}
              <Divider sx={{ my: 1 }} />
              <Typography variant='caption' color='text.secondary'>
                Auf der Speisekarte (Standorte)
              </Typography>
              <Box sx={{ mt: 0.5 }}>{renderAssignmentControl(upsell)}</Box>
            </Paper>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Kategorie</TableCell>
                <TableCell>Preis</TableCell>
                <TableCell>Beschreibung</TableCell>
                <TableCell>Auf der Speisekarte (Standorte)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {upsells.map(upsell => (
                <TableRow key={upsell._id} hover>
                  <TableCell>
                    <Typography variant='body2' fontWeight={600}>
                      {upsell.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2' color='text.secondary'>
                      {upsell.category || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatPrice(upsell.price)}</TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant='body2' color='text.secondary' noWrap title={upsell.descriptionShort || upsell.description}>
                      {upsell.descriptionShort || upsell.description}
                    </Typography>
                  </TableCell>
                  <TableCell>{renderAssignmentControl(upsell)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default MenuItems;
